import React, { useEffect, useMemo, useState } from 'react';
import {
  Newspaper, Database, Mail, BarChart3, Radio, Video,
  RefreshCw, ExternalLink, ChevronLeft, ChevronRight, ShieldCheck
} from 'lucide-react';
import mediaApi from '../../services/mediaApi';

const MediaCockpit = ({ projectId, onTabSwitch }) => {
  const [section, setSection] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [contents, setContents] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingContents, setLoadingContents] = useState(false);
  const [error, setError] = useState(null);

  const limit = 50;

  const loadOverview = async () => {
    setLoadingOverview(true);
    setError(null);
    try {
      const [ov, met] = await Promise.all([
        mediaApi.overview(projectId),
        mediaApi.metrics(projectId),
      ]);
      setOverview(ov);
      setMetrics(met);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Lecture média indisponible');
    } finally {
      setLoadingOverview(false);
    }
  };

  useEffect(() => {
    if (projectId) loadOverview();
  }, [projectId]);

  useEffect(() => {
    if (section !== 'contents' || !projectId) return;
    let cancelled = false;
    setLoadingContents(true);
    mediaApi.contents(projectId, { limit, offset })
      .then((data) => {
        if (!cancelled) setContents(data);
      })
      .catch((e) => {
        if (!cancelled) setContents({
          status: 'unavailable',
          error: e?.response?.data?.error || e.message,
          items: [],
          total: null,
        });
      })
      .finally(() => {
        if (!cancelled) setLoadingContents(false);
      });
    return () => { cancelled = true; };
  }, [section, projectId, offset]);

  const audienceStatus = useMemo(() => {
    const a = metrics || overview?.audience;
    if (!a) return { label: 'Inconnu', detail: 'Source non chargée' };
    if (a.status === 'ready') {
      return {
        label: 'Prêt',
        detail: a.last_complete_day ? 'Dernier jour complet : ' + a.last_complete_day : 'Données disponibles',
      };
    }
    if (a.status === 'collecting') {
      return {
        label: 'Collecte en cours',
        detail: a.baseline_available
          ? 'Baseline partielle disponible, volontairement non affichée'
          : 'Premier jour complet en attente',
      };
    }
    if (a.status === 'unconfigured') return { label: 'Non configuré', detail: 'Aucune source audience liée à ce projet' };
    return { label: 'Indisponible', detail: a.reason || 'Source temporairement indisponible' };
  }, [metrics, overview]);

  const cardStyle = {
    background: 'var(--card-bg)',
    border: '1px solid var(--card-border)',
    borderRadius: 12,
    padding: 16,
  };

  const soft = { color: 'var(--text-secondary)' };
  const muted = { color: 'var(--text-muted)' };

  if (loadingOverview && !overview) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-secondary)' }}>
        <RefreshCw size={26} style={{ animation: 'spin 1s linear infinite', marginBottom: 10 }} />
        <div>Chargement du cockpit média…</div>
      </div>
    );
  }

  const migration = overview?.migration || {};
  const mailing = overview?.mailing || {};
  const source = overview?.connectors?.source;
  const target = overview?.connectors?.target;
  const social = overview?.channels?.social;
  const studio = overview?.channels?.studio;

  const tabs = [
    { id: 'overview', label: 'Vue d’ensemble' },
    { id: 'contents', label: 'Contenus' },
    { id: 'audience', label: 'Audience' },
  ];

  const statusPill = (label, positive = false) => (
    <span style={{
      fontSize: 11,
      padding: '3px 8px',
      borderRadius: 999,
      border: '1px solid ' + (positive ? 'rgba(34,197,94,.35)' : 'var(--card-border)'),
      background: positive ? 'rgba(34,197,94,.10)' : 'var(--input-bg)',
      color: positive ? '#22c55e' : 'var(--text-secondary)',
    }}>{label}</span>
  );

  const summaryCard = (icon, title, value, detail, action) => (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        {icon}
        <div style={{ fontWeight: 600, color: 'var(--text)' }}>{title}</div>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 12, ...soft, minHeight: 34 }}>{detail}</div>
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 1180 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: 16, flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Newspaper size={24} color="#2563eb" />
            <h2 style={{ margin: 0, fontSize: 22, color: 'var(--text)' }}>Cockpit Média</h2>
            {statusPill('Lecture seule', true)}
          </div>
          <div style={{ marginTop: 6, fontSize: 13, ...soft }}>
            Composer le site, la diffusion et la mesure sans dupliquer les outils existants.
          </div>
        </div>
        <button onClick={loadOverview} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px',
          borderRadius: 8, border: '1px solid var(--card-border)',
          background: 'var(--input-bg)', color: 'var(--text-secondary)', cursor: 'pointer'
        }}>
          <RefreshCw size={14} /> Actualiser
        </button>
      </div>

      {error && (
        <div style={{ ...cardStyle, borderColor: 'rgba(239,68,68,.35)', color: '#ef4444' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setSection(t.id)} style={{
            padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
            border: '1px solid ' + (section === t.id ? 'rgba(37,99,235,.35)' : 'var(--card-border)'),
            background: section === t.id ? 'rgba(37,99,235,.12)' : 'var(--input-bg)',
            color: section === t.id ? '#3b82f6' : 'var(--text-secondary)',
            fontWeight: section === t.id ? 600 : 400,
          }}>{t.label}</button>
        ))}
      </div>

      {section === 'overview' && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: 12,
          }}>
            {summaryCard(
              <Database size={19} color="#8b5cf6" />,
              'Migration',
              migration.configured ? String(migration.total || 0) : '—',
              migration.configured
                ? 'migrés : ' + String(migration.by_status?.migrated || 0) +
                  ' · erreurs : ' + String(migration.by_status?.error || 0)
                : 'Aucune source WordPress liée',
              source && onTabSwitch ? (
                <button onClick={() => onTabSwitch('pilot-mutation')} style={{
                  border: 0, background: 'transparent', color: '#3b82f6',
                  padding: 0, cursor: 'pointer', fontSize: 12
                }}>Ouvrir Pilot Mutation</button>
              ) : null
            )}

            {summaryCard(
              <Mail size={19} color="#f59e0b" />,
              'Mailing',
              mailing.module_enabled ? String(mailing.lists || 0) + ' liste(s)' : 'Inactif',
              mailing.module_enabled
                ? String(mailing.subscribers_aggregate || 0) + ' abonnés agrégés · ' +
                  String(Object.values(mailing.campaigns_by_status || {}).reduce((a, b) => a + b, 0)) + ' campagne(s)'
                : 'Module Mailing non activé pour ce projet'
            )}

            {summaryCard(
              <BarChart3 size={19} color="#22c55e" />,
              'Audience',
              audienceStatus.label,
              audienceStatus.detail,
              metrics?.status === 'ready' ? (
                <button onClick={() => setSection('audience')} style={{
                  border: 0, background: 'transparent', color: '#3b82f6',
                  padding: 0, cursor: 'pointer', fontSize: 12
                }}>Voir les agrégats</button>
              ) : null
            )}

            {summaryCard(
              <Radio size={19} color="#ef4444" />,
              'Réseaux',
              social?.configured ? 'Configuré' : 'À brancher',
              social?.configured
                ? 'Connecteur présent, livraison distante non encore attestée'
                : 'Multipublication non active dans ce cockpit'
            )}

            {summaryCard(
              <Video size={19} color="#06b6d4" />,
              'Studio',
              studio?.configured ? 'Disponible' : 'Non configuré',
              studio?.configured
                ? 'Le lien contextuel Studio viendra dans un lot ultérieur'
                : 'Aucune brique Studio exposée ici'
            )}

            {summaryCard(
              <BarChart3 size={19} color="#2563eb" />,
              'Analytics ITRIIA',
              'Projet',
              'Coûts, fichiers, sessions et activité restent dans le module Analytics existant.',
              onTabSwitch ? (
                <button onClick={() => onTabSwitch('analytics')} style={{
                  border: 0, background: 'transparent', color: '#3b82f6',
                  padding: 0, cursor: 'pointer', fontSize: 12
                }}>Ouvrir Analytics</button>
              ) : null
            )}
          </div>

          <div style={{ ...cardStyle, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text)' }}>Source connectée</div>
              <div style={{ fontSize: 12, ...soft }}>{source?.url || 'Aucune'}</div>
              {source && <div style={{ marginTop: 6 }}>{statusPill(source.status || 'inconnu', source.status === 'active')}</div>}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text)' }}>Site observé</div>
              <div style={{ fontSize: 12, ...soft }}>{overview?.observed_site?.url || target?.url || 'Aucun'}</div>
              {(overview?.observed_site?.url || target) && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                  {statusPill(
                    overview?.observed_site?.source === 'media_registry' ? 'Lecture dédiée' : (target?.status || 'inconnu'),
                    overview?.observed_site?.source === 'media_registry' || target?.status === 'active'
                  )}
                  {(overview?.observed_site?.url || target?.url) && (
                    <a href={overview?.observed_site?.url || target?.url} target="_blank" rel="noreferrer" style={{
                      color: '#3b82f6', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11
                    }}><ExternalLink size={11} /> ouvrir</a>
                  )}
                </div>
              )}
            </div>
          </div>

          <div style={{ ...cardStyle, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <ShieldCheck size={20} color="#22c55e" style={{ marginTop: 1 }} />
            <div>
              <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13 }}>Mode lecture seule</div>
              <div style={{ fontSize: 12, ...soft, marginTop: 3 }}>
                Ce cockpit ne publie, n’envoie et ne modifie rien. Les actions réelles restent dans leurs modules propriétaires et sous contrôle HITL.
              </div>
            </div>
          </div>
        </>
      )}

      {section === 'contents' && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div>
              <div style={{ fontWeight: 600, color: 'var(--text)' }}>Contenus de la cible</div>
              <div style={{ fontSize: 11, ...muted }}>Métadonnées uniquement · corps jamais chargé dans le cockpit</div>
            </div>
            {contents?.total != null && statusPill(String(contents.total) + ' contenu(s)', true)}
          </div>

          {loadingContents ? (
            <div style={{ padding: 30, textAlign: 'center', ...soft }}>Lecture de la cible…</div>
          ) : contents?.status !== 'ready' ? (
            <div style={{ padding: 24, textAlign: 'center', ...soft }}>
              {contents?.status === 'no_site_target'
                ? 'Aucune cible Site Internet connectée.'
                : 'La cible ne permet pas actuellement une lecture GET stricte des contenus.'}
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                      <th style={{ padding: '8px 6px' }}>Titre</th>
                      <th style={{ padding: '8px 6px' }}>Rubrique</th>
                      <th style={{ padding: '8px 6px' }}>Date</th>
                      <th style={{ padding: '8px 6px' }}>État</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(contents.items || []).map((item, i) => (
                      <tr key={item.id || item.slug || i} style={{ borderTop: '1px solid var(--card-border)' }}>
                        <td style={{ padding: '9px 6px', color: 'var(--text)', maxWidth: 520 }}>
                          <div style={{ fontWeight: 500 }}>{item.title || item.slug || 'Sans titre'}</div>
                          <div style={{ fontSize: 10, ...muted }}>{item.legacy_path || item.slug || ''}</div>
                        </td>
                        <td style={{ padding: '9px 6px', ...soft }}>{item.rubrique || '—'}</td>
                        <td style={{ padding: '9px 6px', ...soft }}>{item.date_publication || '—'}</td>
                        <td style={{ padding: '9px 6px' }}>{statusPill(item.actif ? 'Publié' : 'Brouillon', item.actif)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14 }}>
                <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} style={{
                  display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px',
                  borderRadius: 7, border: '1px solid var(--card-border)',
                  background: 'var(--input-bg)', color: 'var(--text-secondary)',
                  cursor: offset === 0 ? 'not-allowed' : 'pointer', opacity: offset === 0 ? .5 : 1
                }}><ChevronLeft size={14} /> Précédent</button>
                <button
                  disabled={contents.total == null || offset + limit >= contents.total}
                  onClick={() => setOffset(offset + limit)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px',
                    borderRadius: 7, border: '1px solid var(--card-border)',
                    background: 'var(--input-bg)', color: 'var(--text-secondary)',
                    cursor: contents.total != null && offset + limit < contents.total ? 'pointer' : 'not-allowed',
                    opacity: contents.total != null && offset + limit < contents.total ? 1 : .5
                  }}
                >Suivant <ChevronRight size={14} /></button>
              </div>
            </>
          )}
        </div>
      )}

      {section === 'audience' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {metrics?.status !== 'ready' ? (
            <div style={cardStyle}>
              <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>{audienceStatus.label}</div>
              <div style={{ fontSize: 12, ...soft }}>{audienceStatus.detail}</div>
              <div style={{ fontSize: 11, ...muted, marginTop: 10 }}>
                Aucun chiffre de tendance n’est affiché tant qu’une journée complète qualifiée n’est pas disponible.
              </div>
            </div>
          ) : (
            <>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12
              }}>
                {summaryCard(
                  <BarChart3 size={18} color="#22c55e" />,
                  'GET publics non-bot',
                  String(metrics.metrics?.successful_public_gets?.nonbot || 0),
                  'Requêtes GET 200/304 · ni visiteurs uniques ni humains vérifiés'
                )}
                {summaryCard(
                  <Database size={18} color="#8b5cf6" />,
                  'Bots connus',
                  String(metrics.metrics?.known_bot_requests || 0),
                  'Signatures crawler connues sur la journée'
                )}
                {summaryCard(
                  <ShieldCheck size={18} color="#2563eb" />,
                  'Qualité',
                  metrics.quality?.coverage_ratio != null
                    ? Math.round(metrics.quality.coverage_ratio * 100) + '%'
                    : '—',
                  'Couverture de parsing · dernier jour complet : ' + (metrics.last_complete_day || '—')
                )}
              </div>

              <div style={{ ...cardStyle, display: 'grid', gridTemplateColumns: '1.2fr .8fr', gap: 18 }}>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>Articles les plus demandés</div>
                  {(metrics.metrics?.top_articles || []).slice(0, 10).map((x, i) => (
                    <div key={x.canonical || i} style={{
                      display: 'flex', justifyContent: 'space-between', gap: 12,
                      padding: '7px 0', borderTop: i ? '1px solid var(--card-border)' : 'none'
                    }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {x.title || x.canonical}
                        </div>
                        <div style={{ fontSize: 10, ...muted }}>{x.rubrique || ''}</div>
                      </div>
                      <div style={{ fontWeight: 700, color: '#22c55e' }}>{x.nonbot_successful_gets}</div>
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>Provenances externes</div>
                  {(metrics.metrics?.external_referrers || []).slice(0, 10).map((x, i) => (
                    <div key={x.host || i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                      <span style={{ fontSize: 12, ...soft }}>{x.host}</span>
                      <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 600 }}>{x.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MediaCockpit;
